// All canvas drawing: entities (vector placeholders — step 2 swaps in the watercolor
// sprites), the mash zoom, the finale, the camera overlay, blade trail, floating text,
// and the HUD.
import { G } from '../game/state';
import { MAX_LIVES, MEGA_CAP, MEGA_TIME, POM_TIME, PRIO, ROUND } from '../game/config';
import type { Prio } from '../game/types';
import { mashCenter } from '../game/scoring';
import { getSprite } from '../assets/sprites';
import { CREAM } from './background';
import { videoCoverRect } from '../input/videoMap';

// UI/HUD font — Ancizar Serif everywhere (loaded via Google Fonts in index.html).
const UI = "'Ancizar Serif', Georgia, serif";

// Blade trail + fingertip cursor colour: soft baby blue so it reads watercolour-light against
// the cream paper (not the heavy navy from the prototype).
const BLADE = '168,216,240';

// Draw a watercolor sprite centered at (x,y), sized to ~2r wide (aspect from the source),
// with a subtle dark shadow so soft watercolor edges read against the cream paper (§2).
function drawSprite(img: HTMLImageElement, x: number, y: number, r: number, rot: number, alpha = 1, scale = 1.14): void {
  const ctx = G.ctx;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(rot);
  const w = 2 * r * scale;
  const h = w * (img.naturalHeight / img.naturalWidth);
  ctx.shadowColor = 'rgba(0,0,0,.22)';
  ctx.shadowBlur = Math.max(2, r * 0.11);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();
}

// Duplicate-Storm sprite (single-sprite fallback for all mash states) or vector fallback.
function drawPomAt(x: number, y: number, r: number, rot: number): void {
  const spr = getSprite('dupes-full');
  if (spr) drawSprite(spr, x, y, r, rot, 1, 1.18);
  else drawPom(x, y, r, rot);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawTicketCard(x: number, y: number, r: number, rot: number, color: string, label: string, txtColor: string, clip: number): void {
  const ctx = G.ctx;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  const w = r * 2;
  const h = r * 1.4;
  if (clip) {
    ctx.beginPath();
    ctx.rect(clip > 0 ? 0 : -w, -h, w, h * 2);
    ctx.clip();
  }
  ctx.fillStyle = '#fdfdfc';
  roundRect(ctx, -w / 2, -h / 2, w, h, 8);
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,.35)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = color;
  roundRect(ctx, -w / 2, -h / 2, w * 0.34, h, 8);
  ctx.fill();
  ctx.fillStyle = txtColor;
  ctx.font = 'bold ' + r * 0.5 + 'px ' + UI;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, -w / 2 + w * 0.17, 0);
  ctx.strokeStyle = '#c9cdd6';
  ctx.lineWidth = 2;
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath();
    ctx.moveTo(-w * 0.08, i * h * 0.22);
    ctx.lineTo(w * 0.38, i * h * 0.22);
    ctx.stroke();
  }
  ctx.restore();
}

function drawAngry(o: { x: number; y: number; r: number; rot?: number; happy?: boolean }): void {
  const ctx = G.ctx;
  ctx.save();
  ctx.translate(o.x, o.y);
  ctx.rotate((o.rot ?? 0) * 0.3);
  ctx.fillStyle = o.happy ? '#5cc78a' : '#e5484d';
  ctx.beginPath();
  ctx.arc(0, 0, o.r, 0, 7);
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,.3)';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(-o.r * 0.32, -o.r * 0.15, o.r * 0.11, 0, 7);
  ctx.arc(o.r * 0.32, -o.r * 0.15, o.r * 0.11, 0, 7);
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 4;
  ctx.beginPath();
  if (o.happy) {
    ctx.arc(0, o.r * 0.15, o.r * 0.35, 0.3, 2.8);
  } else {
    ctx.arc(0, o.r * 0.65, o.r * 0.35, 3.6, 5.8);
  }
  ctx.stroke();
  if (!o.happy) {
    ctx.beginPath();
    ctx.moveTo(-o.r * 0.5, -o.r * 0.42);
    ctx.lineTo(-o.r * 0.14, -o.r * 0.3);
    ctx.moveTo(o.r * 0.5, -o.r * 0.42);
    ctx.lineTo(o.r * 0.14, -o.r * 0.3);
    ctx.stroke();
    // steam
    ctx.globalAlpha = 0.5 + 0.4 * Math.sin(performance.now() / 120);
    ctx.font = 'bold ' + o.r * 0.6 + 'px ' + UI;
    ctx.textAlign = 'center';
    ctx.fillText('💢', 0, -o.r * 1.25);
    ctx.globalAlpha = 1;
  }
  ctx.restore();
}

function drawChute(o: { x: number; y: number; r: number; sway?: number }): void {
  const ctx = G.ctx;
  ctx.save();
  ctx.translate(o.x, o.y);
  ctx.rotate(Math.sin(o.sway ?? 0) * 0.12);
  ctx.fillStyle = '#2fae72';
  ctx.beginPath();
  ctx.arc(0, -o.r * 1.1, o.r, Math.PI, 0);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.7)';
  ctx.lineWidth = 2;
  for (const s of [-1, -0.35, 0.35, 1]) {
    ctx.beginPath();
    ctx.moveTo(s * o.r * 0.9, -o.r * 1.05);
    ctx.lineTo(0, o.r * 0.25);
    ctx.stroke();
  }
  ctx.fillStyle = '#0e1018';
  roundRect(ctx, -o.r * 0.5, o.r * 0.2, o.r, o.r * 0.8, 6);
  ctx.fill();
  ctx.fillStyle = '#6ee7a0';
  ctx.font = 'bold ' + o.r * 0.5 + 'px ' + UI;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('P', 0, o.r * 0.6);
  ctx.restore();
}

function drawPom(x: number, y: number, r: number, rot: number): void {
  const ctx = G.ctx;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  for (const s of [
    [-r * 0.18, r * 0.14, -0.12],
    [r * 0.15, -r * 0.1, 0.1],
    [0, 0, 0],
  ]) {
    ctx.save();
    ctx.translate(s[0], s[1]);
    ctx.rotate(s[2]);
    ctx.fillStyle = '#8e4ec6';
    roundRect(ctx, -r, -r * 0.7, r * 2, r * 1.4, 8);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,.35)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold ' + r * 0.4 + 'px ' + UI;
  ctx.fillText('DUPES', 0, -r * 0.12);
  ctx.font = 'bold ' + r * 0.24 + 'px ' + UI;
  ctx.fillStyle = '#7c3aed';
  ctx.fillText('same issue ×99', 0, r * 0.3);
  ctx.restore();
}

export function render(): void {
  const ctx = G.ctx;
  const W = G.W;
  const H = G.H;
  ctx.save();
  if (G.shake > 0) ctx.translate((Math.random() - 0.5) * G.shake * 22, (Math.random() - 0.5) * G.shake * 22);
  ctx.fillStyle = CREAM;
  ctx.fillRect(-30, -30, W + 60, H + 60); // base behind shake offsets
  if (G.bgCanvas) ctx.drawImage(G.bgCanvas, 0, 0); // cream paper texture (web-background.png)
  if (G.camMode && G.video.readyState >= 2 && G.video.videoWidth) {
    // Dimmed, unzoomed mirror of the player, drawn with the SAME cover-fit transform the hand
    // mapping uses (videoCoverRect) — so the fingertip pixel here and the cursor are one point.
    const { dw, dh, ox, oy } = videoCoverRect(W, H, G.video.videoWidth, G.video.videoHeight);
    ctx.save();
    ctx.globalAlpha = G.state === 'camwait' ? 0.4 : 0.18;
    ctx.translate(W, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(G.video, ox, oy, dw, dh);
    ctx.restore();
  }

  for (const h of G.halves) {
    const life = Math.min(1, h.life);
    const spr = h.prio ? getSprite(`ticket-${h.prio}-${h.side < 0 ? 'left' : 'right'}` as const) : null;
    if (spr) {
      // Each torn-half PNG is the full-card canvas with only its side painted, so it draws
      // at the same box as the whole card and peels away 1:1 with the physics.
      drawSprite(spr, h.x, h.y, h.r, h.rot, life);
    } else {
      ctx.globalAlpha = life;
      drawTicketCard(h.x, h.y, h.r, h.rot, h.prio ? PRIO[h.prio].color : h.color, h.prio || '', h.prio ? PRIO[h.prio].txt : '#fff', h.side);
      ctx.globalAlpha = 1;
    }
  }
  for (const o of G.objs) {
    if (o.dead) continue;
    if (o.kind === 'ticket') {
      const spr = getSprite(`ticket-${o.prio as Prio}`);
      if (spr) drawSprite(spr, o.x, o.y, o.r, o.rot ?? 0);
      else drawTicketCard(o.x, o.y, o.r, o.rot ?? 0, PRIO[o.prio as Prio].color, o.prio as string, PRIO[o.prio as Prio].txt, 0);
    } else if (o.kind === 'angry') {
      const spr = getSprite(o.happy ? 'customer-happy' : 'customer-angry');
      if (spr) drawSprite(spr, o.x, o.y, o.r, (o.rot ?? 0) * 0.3, 1, 1.08);
      else drawAngry(o); // vector fallback if the sprite hasn't loaded / is missing
    } else if (o.kind === 'pom') drawPomAt(o.x, o.y, o.r, o.rot ?? 0);
    else {
      const spr = getSprite('parachute');
      if (spr) drawSprite(spr, o.x, o.y, o.r, Math.sin(o.sway ?? 0) * 0.12, 1, 1.25);
      else drawChute(o);
    }
  }
  for (const p of G.parts) {
    ctx.save();
    ctx.globalAlpha = Math.max(0, p.life);
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
    ctx.restore();
  }

  if (G.state === 'play' && G.mash) {
    // zoom-in: white-out the frozen world, blow up the dupe stack
    ctx.fillStyle = 'rgba(250,249,246,' + 0.6 * G.mash.zoom + ')';
    ctx.fillRect(-30, -30, W + 60, H + 60);
    const c = mashCenter();
    const wob = 1 + Math.sin(performance.now() / 60) * 0.03;
    drawPomAt(c.x, c.y, c.r * wob, Math.sin(performance.now() / 250) * 0.06);
    ctx.globalAlpha = G.mash.zoom;
    ctx.fillStyle = '#1f2430';
    ctx.font = 'bold 26px ' + UI;
    ctx.textAlign = 'center';
    ctx.fillText('MASH! MERGE THE DUPLICATES  ' + G.mash.hits + ' dupes merged', W / 2, H * 0.14);
    ctx.fillStyle = '#6b7280';
    ctx.font = '16px ' + UI;
    ctx.fillText(Math.max(0, POM_TIME - G.mash.t).toFixed(1) + 's', W / 2, H * 0.175);
    ctx.globalAlpha = 1;
  }

  if (G.state === 'finale') {
    const m = { x: W / 2, y: H * 0.45, r: Math.min(W, H) * 0.22 };
    const wob = 1 + Math.sin(performance.now() / 70) * 0.02;
    ctx.save();
    ctx.translate(m.x, m.y);
    ctx.scale(wob, wob);
    // Mega-Ticket finale art is missing → scale up the P0 sprite (or vector P0) per §2.
    const megaSpr = getSprite('ticket-P0');
    if (megaSpr) drawSprite(megaSpr, 0, 0, m.r, Math.sin(performance.now() / 300) * 0.05, 1, 1.15);
    else drawTicketCard(0, 0, m.r, Math.sin(performance.now() / 300) * 0.05, '#e5484d', 'P0', '#fff', 0);
    ctx.fillStyle = '#12141c';
    ctx.font = 'bold ' + m.r * 0.14 + 'px ' + UI;
    ctx.textAlign = 'center';
    ctx.fillText('THE NEVER-ENDING THREAD', 0, -m.r * 0.85);
    ctx.fillText('re: re: re: fwd: URGENT', 0, m.r * 0.95);
    ctx.restore();
    ctx.fillStyle = '#1f2430';
    ctx.font = 'bold 28px ' + UI;
    ctx.textAlign = 'center';
    ctx.fillText('MASH-SLICE!  ' + G.megaHits + ' / ' + MEGA_CAP, W / 2, H * 0.12);
    ctx.fillStyle = '#6b7280';
    ctx.font = '16px ' + UI;
    ctx.fillText(Math.max(0, MEGA_TIME - G.finaleT).toFixed(1) + 's', W / 2, H * 0.155);
  }

  if (G.state === 'camwait') {
    ctx.textAlign = 'center';
    ctx.fillStyle = '#1f2430';
    ctx.font = 'bold 28px ' + UI;
    ctx.fillText(
      G.camErr ? 'Camera error: ' + G.camErr : G.camReady ? 'Point your index finger ☝️ to start' : 'Loading hand tracking…',
      W / 2,
      H * 0.42,
    );
    if (G.camReady && !G.camErr && G.raiseFrames > 0) {
      ctx.fillStyle = 'rgba(0,0,0,.1)';
      ctx.fillRect(W / 2 - 100, H * 0.47, 200, 8);
      ctx.fillStyle = '#1e3a8a';
      ctx.fillRect(W / 2 - 100, H * 0.47, 200 * Math.min(1, G.raiseFrames / 20), 8);
    }
    ctx.fillStyle = '#6b7280';
    ctx.font = '15px ' + UI;
    ctx.fillText('index finger out = blade ON · fist = blade OFF · both hands work', W / 2, H * 0.55);
  }

  if (G.camMode) {
    for (const h of G.camHands) {
      if (!h.p) continue; // fingertip cursors
      if (h.active) {
        // armed: soft baby-blue glow
        const g = ctx.createRadialGradient(h.p.x, h.p.y, 2, h.p.x, h.p.y, 20);
        g.addColorStop(0, `rgba(${BLADE},.95)`);
        g.addColorStop(1, `rgba(${BLADE},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(h.p.x, h.p.y, 20, 0, 7);
        ctx.fill();
      } else {
        // sheathed (fist): hollow gray ring
        ctx.strokeStyle = 'rgba(180,190,210,.5)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(h.p.x, h.p.y, 12, 0, 7);
        ctx.stroke();
      }
    }
  }

  // blade trail — a smooth baby-blue ribbon: quadratic curves through segment midpoints
  // (Catmull-Rom-equivalent), round caps/joins, width + alpha tapering from tail to head.
  if (G.trail.length > 2) {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const n = G.trail.length;
    for (let i = 1; i < n - 1; i++) {
      const a = i / n; // 0 at the tail → 1 at the head
      const mx1 = (G.trail[i - 1].x + G.trail[i].x) / 2;
      const my1 = (G.trail[i - 1].y + G.trail[i].y) / 2;
      const mx2 = (G.trail[i].x + G.trail[i + 1].x) / 2;
      const my2 = (G.trail[i].y + G.trail[i + 1].y) / 2;
      ctx.strokeStyle = `rgba(${BLADE},${a * 0.85})`;
      ctx.lineWidth = 2 + a * 9; // keep a little body at the tail so it reads as a ribbon
      ctx.beginPath();
      ctx.moveTo(mx1, my1);
      ctx.quadraticCurveTo(G.trail[i].x, G.trail[i].y, mx2, my2);
      ctx.stroke();
    }
  } else if (G.trail.length === 2) {
    ctx.strokeStyle = `rgba(${BLADE},.6)`;
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(G.trail[0].x, G.trail[0].y);
    ctx.lineTo(G.trail[1].x, G.trail[1].y);
    ctx.stroke();
  }

  for (const x of G.texts) {
    ctx.globalAlpha = Math.max(0, Math.min(1, x.life));
    ctx.fillStyle = x.color;
    ctx.font = (x.big ? 'bold ' : '600 ') + (x.big ? Math.min(30, W * 0.05) : 16) + 'px ' + UI;
    ctx.textAlign = 'center';
    ctx.fillText(x.str, x.x, x.y);
    ctx.globalAlpha = 1;
  }

  if (G.camMode && G.debug) {
    ctx.fillStyle = 'rgba(139,147,167,.85)';
    ctx.font = '11px ' + UI;
    ctx.textAlign = 'left';
    // detection rate + per-frame detect time (GPU ~5-15ms, CPU fallback ~30-60ms) + path
    ctx.fillText(`cam ${G.detFps} fps · det ${G.detMs.toFixed(1)} ms · ${G.camPath || '…'}`, 12, H - 12);
  }

  // HUD
  if (G.state === 'play' || G.state === 'finale') {
    ctx.fillStyle = '#1f2430';
    ctx.font = 'bold 30px ' + UI;
    ctx.textAlign = 'left';
    ctx.fillText(String(G.score), 18, 42);
    ctx.font = '11px ' + UI;
    ctx.fillStyle = '#6b7280';
    ctx.fillText('TICKETS RESOLVED: ' + G.resolved, 18, 60);
    if (G.state === 'play') {
      const rem = Math.max(0, ROUND - G.t);
      ctx.textAlign = 'center';
      ctx.fillStyle = rem < 10 ? '#dc2626' : '#1f2430';
      ctx.font = 'bold 26px ' + UI;
      ctx.fillText(Math.ceil(rem) + 's', W / 2, 40);
      ctx.textAlign = 'right';
      for (let i = 0; i < MAX_LIVES; i++) {
        const gone = i < G.missed;
        ctx.fillStyle = gone ? '#dc2626' : 'rgba(0,0,0,.14)';
        ctx.beginPath();
        ctx.arc(W - 24 - i * 26, 32, 9, 0, 7);
        ctx.fill();
        if (gone) {
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(W - 29 - i * 26, 27);
          ctx.lineTo(W - 19 - i * 26, 37);
          ctx.moveTo(W - 19 - i * 26, 27);
          ctx.lineTo(W - 29 - i * 26, 37);
          ctx.stroke();
        }
      }
      if (G.t < G.frenzyUntil) {
        ctx.fillStyle = '#16a34a';
        ctx.font = 'bold 18px ' + UI;
        ctx.textAlign = 'center';
        ctx.fillText('⚡ PARAHELP FRENZY ⚡', W / 2, 70);
      }
    }
  }
  ctx.restore();
}
